import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import 'cordova-plugin-purchase';
import * as Sentry from '@sentry/react';
import { getPersistedIapState } from 'features/payments/hooks/useIapState';
import { isApprovedWatchDutyTransaction } from 'features/payments/utils/detectIapCollision';

/**
 * Finishes renewal transactions left in StoreKit's queue. Nothing else does: the
 * `approved`/`verified`/`finished` chain belongs to the checkout page and is torn
 * down with it, so a renewal landing anywhere else is never finished. Apple
 * requires it, and warns that an unfinished queue can prompt the user to
 * authenticate a purchase on every launch.
 *
 * Finish only, never `verify()`: renewals reach the backend through its own
 * webhook, and there is no checkout form out here to validate one with.
 */
const finishStaleTransactions = async (): Promise<void> => {
  // An in-flight checkout belongs to `useReconcileInterruptedIap`, which finds
  // its transaction by APPROVED state, so finishing here first would make an
  // interrupted purchase unrecoverable.
  const pendingCheckoutStartedAt =
    getPersistedIapState()?.checkoutDetails.checkoutStartedAt;

  if (pendingCheckoutStartedAt) {
    return;
  }

  const stale = CdvPurchase.store.localTransactions.filter(
    isApprovedWatchDutyTransaction,
  );

  if (!stale.length) {
    return;
  }

  await Promise.all(
    stale.map(async (transaction) => {
      try {
        await transaction.finish();
      } catch (err) {
        Sentry.captureException(err, {
          extra: { context: 'iap-finish-stale-transaction' },
        });
      }
    }),
  );

  // The only measure of how large the backlog gets in the wild. A count that
  // does not drop across launches would mean the finishes are not taking.
  Sentry.captureMessage('iap-stale-transactions-finished', {
    level: 'info',
    tags: { count: stale.length },
  });
};

export const useFinishStaleIapTransactions = (): void => {
  const [storeReady, setStoreReady] = useState(false);
  const completedRef = useRef(false);

  useEffect(() => {
    if (!storeReady || completedRef.current) {
      return;
    }

    completedRef.current = true;
    finishStaleTransactions();
  }, [storeReady]);

  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios') {
      return undefined;
    }

    const onDeviceReady = (): void => {
      CdvPurchase.store.ready(() => setStoreReady(true));
    };

    document.addEventListener('deviceready', onDeviceReady);
    return () => {
      document.removeEventListener('deviceready', onDeviceReady);
    };
  }, []);
};
